import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';

import { NotFoundPage } from '../NotFoundPage';
import { ExpiredPage } from '../ExpiredPage';
import { LoadingPage } from '../LoadingPage';
import { CanceledPage } from '../CanceledPage';
import { EnvelopeFolderLogin } from '../EnvelopeFolderLogin';
import { EnvelopeFolderProposals } from '../EnvelopeFolderProposals';
import { EnvelopeFolderSigned } from '../EnvelopeFolderSigned';
import { EnvelopeFolderCollection } from '../EnvelopeFolderCollection';
import { EnvelopeFolderChildJourney } from '../EnvelopeFolderChildJourney';
import { PrivatePayrollAuctionLoading } from '../customTheme/PrivatePayrollAuction/Loading';
import { PrivatePayrollAuctionTheme, SocialSecurityTheme } from '../customTheme';
import ErrorBoundaryComponent from '../ErrorBoundary';
import { setDatadogPageAction } from '../../services/datadog';
import { useThemeContext } from '../../contexts/ThemeContext';
import QISignTheme from '../../interfaces/Theme';

const DEFAULT_CLIENT_THEME_SETTINGS: QISignTheme = {
    logo_url: 'https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_azul_claro_escuro_principal.png',
    font_color: '#000000',
    button_color: '#4FCCED',
    email_logo_url: 'https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_branco_email.png',
    background_color: '#0E3573',
};

interface EnvelopeFolderMainContainerProps {
    setSignMetadata?: (metadata: any) => void;
    signMetadata?: any;
}

export const EnvelopeFolderMainContainer = ({
    setSignMetadata,
}: EnvelopeFolderMainContainerProps = {}) => {
    const { clientThemeSettings, setClientThemeSettings } = useThemeContext();
    const { folderHash } = useParams<{ folderHash: string }>();

    const envelopeCookieName = `qisign_folder_envelope_${folderHash}`;
    const [cookies, setCookie] = useCookies([envelopeCookieName]);
    const resumeAttemptedRef = useRef(false);

    const persistCurrentEnvelope = useCallback(
        (envelopeKey: string) => {
            setCookie(envelopeCookieName, envelopeKey, { path: `/ef/${folderHash}`, maxAge: 60 * 60 });
        },
        [setCookie, envelopeCookieName, folderHash],
    );

    const clearCurrentEnvelope = useCallback(() => {
        setCookie(envelopeCookieName, '', { path: `/ef/${folderHash}`, maxAge: 0 });
    }, [setCookie, envelopeCookieName, folderHash]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [expired, setExpired] = useState(false);
    const [canceled, setCanceled] = useState(false);
    const [folderStep, setFolderStep] = useState('');
    const [folderData, setFolderData] = useState<any>({});
    const [folderSessionToken, setFolderSessionToken] = useState<string | undefined>(undefined);
    const [latitude, setLatitude] = useState<number | undefined>(undefined);
    const [longitude, setLongitude] = useState<number | undefined>(undefined);
    const [listing, setListing] = useState<any>(undefined);
    const [folderStatus, setFolderStatus] = useState<string | undefined>(undefined);
    const [completionAvailable, setCompletionAvailable] = useState(false);
    const [selectedEnvelopeKey, setSelectedEnvelopeKey] = useState<string | undefined>(undefined);
    const [selectedDetail, setSelectedDetail] = useState<any>(undefined);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [lastDecision, setLastDecision] = useState<{
        next_step?: string;
        signer_status?: string;
        envelope_key?: string;
        decision?: 'accepted' | 'rejected';
    }>({});
    const [childSession, setChildSession] = useState<{
        envelope_key: string;
        signer_token: string;
        next_step: string;
        ocr_token?: string;
        face_token?: string;
        device_token?: string;
    } | undefined>(undefined);
    const [openingChild, setOpeningChild] = useState(false);
    const [returning, setReturning] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [commitKeptCount, setCommitKeptCount] = useState<number | undefined>(undefined);
    const [commitCanceledCount, setCommitCanceledCount] = useState<number | undefined>(undefined);

    useEffect(() => {
        setDatadogPageAction(`/ef/${folderHash} [@step=${folderStep}]`, {
            folderHash,
            folderStep,
            folderData,
            folderStatus,
            completionAvailable,
        });
    }, [folderHash, folderStep, folderData, folderStatus, completionAvailable]);

    const collectGeoLocation = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                setLatitude(position.coords.latitude);
                setLongitude(position.coords.longitude);
            });
        }
    }, []);

    const buildAuthHeaders = useCallback(
        (token: string): { headers: { Authorization: string } } => ({
            headers: { Authorization: `Bearer ${token}` },
        }),
        [],
    );

    const applyListing = useCallback((data: any) => {
        setListing(data);
        setFolderStatus(data?.status);
        setCompletionAvailable(data?.completion_available === true);
    }, []);

    const fetchListing = useCallback(async () => {
        if (!folderSessionToken) return undefined;
        try {
            const response = await axios.get(
                '/folder_signer',
                buildAuthHeaders(folderSessionToken),
            );
            applyListing(response.data);

            if (
                ['collecting', 'completing'].includes(response.data?.status) &&
                response.data?.next_collection_step
            ) {
                setFolderStep(response.data.next_collection_step);
            }
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 401) {
                setFolderStep('login');
            }
            return undefined;
        }
    }, [folderSessionToken, buildAuthHeaders, applyListing]);

    const refreshListing = useCallback(async () => {
        return fetchListing();
    }, [fetchListing]);

    const returnToProposals = useCallback(async () => {
        clearCurrentEnvelope();
        setChildSession(undefined);
        setSelectedEnvelopeKey(undefined);
        setReturning(true);
        setFolderStep('proposals');
        await refreshListing();
        setReturning(false);
    }, [refreshListing, clearCurrentEnvelope]);

    const openChildSession = useCallback(
        async (envelopeKey: string) => {
            if (!folderSessionToken || !envelopeKey) return;

            setSelectedEnvelopeKey(envelopeKey);
            setOpeningChild(true);

            try {
                const response = await axios.get(
                    '/folder_signer/envelope/' + envelopeKey + '/signer',
                    buildAuthHeaders(folderSessionToken),
                );

                const nextStep: string | null = response.data?.next_step ?? null;
                const signerStatus: string | undefined = response.data?.signer_status;
                const signerToken: string | undefined = response.data?.signer_token;

                if (nextStep === null || signerStatus === 'waiting_folder_conclusion') {
                    clearCurrentEnvelope();
                    setChildSession(undefined);
                    await refreshListing();
                    setFolderStep('proposals');
                    return;
                }

                if (!signerToken) {
                    clearCurrentEnvelope();
                    setChildSession(undefined);
                    await refreshListing();
                    setFolderStep('proposals');
                    return;
                }

                persistCurrentEnvelope(envelopeKey);
                setChildSession({
                    envelope_key: envelopeKey,
                    signer_token: signerToken,
                    next_step: nextStep,
                    ocr_token: response.data?.ocr_token,
                    face_token: response.data?.face_token,
                    device_token: response.data?.device_token,
                });
                setFolderStep('child_journey');
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    setFolderStep('login');
                    return;
                }
                clearCurrentEnvelope();
                setChildSession(undefined);
                await refreshListing();
                setFolderStep('proposals');
            } finally {
                setOpeningChild(false);
            }
        },
        [folderSessionToken, buildAuthHeaders, refreshListing, persistCurrentEnvelope, clearCurrentEnvelope],
    );

    useEffect(() => {
        if (resumeAttemptedRef.current) return;
        if (!folderSessionToken || !listing) return;
        if (folderStep !== 'proposals') return;
        if (childSession || openingChild) return;

        const persistedKey: string | undefined = cookies[envelopeCookieName];
        if (!persistedKey) return;

        resumeAttemptedRef.current = true;
        openChildSession(persistedKey);
    }, [folderSessionToken, listing, folderStep, childSession, openingChild, cookies, envelopeCookieName, openChildSession]);

    const openProposalDetail = useCallback(
        async (envelopeKey: string) => {
            if (!folderSessionToken || !envelopeKey) return;

            setSelectedEnvelopeKey(envelopeKey);
            setSelectedDetail(undefined);
            setLoadingDetail(true);

            try {
                const response = await axios.get(
                    '/folder_signer/envelope/' + envelopeKey,
                    buildAuthHeaders(folderSessionToken),
                );
                setSelectedDetail(response.data);
            } catch (error: any) {
                if (error.response?.status === 401) {
                    setSelectedEnvelopeKey(undefined);
                    setFolderStep('login');
                    return;
                }
            } finally {
                setLoadingDetail(false);
            }
        },
        [folderSessionToken, buildAuthHeaders],
    );

    const refreshProposalDetail = useCallback(
        async (envelopeKey: string | undefined): Promise<any | null> => {
            if (!folderSessionToken || !envelopeKey) return null;
            try {
                const response = await axios.get(
                    '/folder_signer/envelope/' + envelopeKey,
                    buildAuthHeaders(folderSessionToken),
                );
                setSelectedDetail(response.data);
                return response.data;
            } catch (error: any) {
                if (error.response?.status === 401) {
                    setSelectedEnvelopeKey(undefined);
                    setFolderStep('login');
                }
                return null;
            }
        },
        [folderSessionToken, buildAuthHeaders],
    );

    const submitFolderDecision = useCallback(
        async (
            envelopeKey: string | undefined,
            decision: 'accepted' | 'rejected',
            step: string = 'resume_operation',
        ) => {
            if (!folderSessionToken || !envelopeKey) return;

            const clearSelection = () => {
                setSelectedEnvelopeKey(undefined);
                setSelectedDetail(undefined);
            };

            try {
                const response = await axios.patch(
                    '/folder_signer/envelope/' + envelopeKey,
                    { step, decision },
                    buildAuthHeaders(folderSessionToken),
                );
                await refreshListing();
                setLastDecision({
                    next_step: response.data?.next_step,
                    signer_status: response.data?.signer_status,
                    envelope_key: envelopeKey,
                    decision: decision === 'rejected' ? 'rejected' : 'accepted',
                });
                clearSelection();
            } catch (error: any) {
                const status = error.response?.status;
                if (status === 401) {
                    clearSelection();
                    setFolderStep('login');
                    return;
                }
                if (status === 409) {
                    await refreshListing();
                    clearSelection();
                }
            }
        },
        [folderSessionToken, buildAuthHeaders, refreshListing],
    );

    useEffect(() => {
        if (folderStep === 'proposals' && listing === undefined) {
            fetchListing();
        }
    }, [folderStep, listing, fetchListing]);

    useEffect(() => {
        if (!setSignMetadata || !listing) return;
        const collectionFace = (listing.collection_steps || []).some(
            (step: any) => step?.step === 'face_recognition',
        );
        const childFace = (listing.envelopes || []).some(
            (envelope: any) => envelope?.signature_settings?.face === true,
        );
        if (collectionFace || childFace) {
            setSignMetadata({ face: true });
        }
    }, [listing, setSignMetadata]);

    useEffect(() => {
        if (lastDecision.decision !== 'accepted') return;
        if (!lastDecision.next_step || lastDecision.signer_status !== 'on_signature') return;

        const envelopeKey = lastDecision.envelope_key ?? selectedEnvelopeKey;
        if (!envelopeKey) return;

        setLastDecision({});
        openChildSession(envelopeKey);
    }, [lastDecision, selectedEnvelopeKey, openChildSession]);

    useEffect(() => {
        collectGeoLocation();

        axios
            .get('/folder_signer/hash/' + folderHash)
            .then((response) => {
                setFolderData(response.data);
                const themeSettings: QISignTheme = response.data.client_theme_settings
                    ? response.data.client_theme_settings
                    : DEFAULT_CLIENT_THEME_SETTINGS;
                setClientThemeSettings(themeSettings);

                if (response.data.status === 'canceled') {
                    setCanceled(true);
                } else if (response.data.status === 'expired') {
                    setExpired(true);
                } else {
                    setFolderStep('login');
                }
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [folderHash, collectGeoLocation, setClientThemeSettings]);

    const handleCommit = useCallback(() => {
        if (!folderSessionToken) return;

        clearCurrentEnvelope();
        setCommitting(true);

        axios
            .patch(
                '/folder_signer/complete',
                {},
                buildAuthHeaders(folderSessionToken),
            )
            .then(async (response) => {
                const data = response.data;
                setCommitKeptCount(data?.kept_count);
                setCommitCanceledCount(data?.canceled_count);

                if (data?.status === 'collecting' && data?.next_collection_step) {
                    await refreshListing();
                } else {
                    applyListing(data);
                    setFolderStep('signed');
                }
            })
            .catch(async (error: any) => {
                const status = error.response?.status;
                if (status === 401) {
                    setFolderStep('login');
                    return;
                }
                if (status === 409) {
                    const message: string = error.response?.data?.message ?? '';
                    await refreshListing();
                    if (/already committed/i.test(message)) {
                        setFolderStep('signed');
                    }
                }
            })
            .finally(() => {
                setCommitting(false);
            });
    }, [folderSessionToken, buildAuthHeaders, applyListing, refreshListing, clearCurrentEnvelope]);

    const getScenes = () => {
        if (loading) {
            return <LoadingPage />;
        }

        if (error) {
            return <NotFoundPage />;
        }

        if (canceled || folderStatus === 'canceled') {
            return <CanceledPage />;
        }

        if (expired || folderStatus === 'expired') {
            return <ExpiredPage />;
        }

        if (committing || openingChild || returning) {
            return <PrivatePayrollAuctionLoading />;
        }

        if (folderStep === 'child_journey' && childSession) {
            const resolvedTheme: QISignTheme = clientThemeSettings ?? DEFAULT_CLIENT_THEME_SETTINGS;
            return (
                <EnvelopeFolderChildJourney
                    childSignerToken={childSession.signer_token}
                    initialStep={childSession.next_step}
                    sessionId={childSession.envelope_key}
                    sessionTokens={{
                        ocr_token: childSession.ocr_token,
                        face_token: childSession.face_token,
                        device_token: childSession.device_token,
                    }}
                    resolvedTheme={resolvedTheme}
                    setSignMetadata={setSignMetadata}
                    onJourneyGated={returnToProposals}
                    onBack={returnToProposals}
                    setSessionExpired={() => {
                        setChildSession(undefined);
                        setSelectedEnvelopeKey(undefined);
                        setFolderStep('login');
                    }}
                />
            );
        }

        if (folderStep === 'signed' || folderStatus === 'completing' || folderStatus === 'completed') {
            const resolvedTheme: QISignTheme = clientThemeSettings ?? DEFAULT_CLIENT_THEME_SETTINGS;
            return (
                <EnvelopeFolderSigned
                    resolvedTheme={resolvedTheme}
                    keptCount={commitKeptCount}
                    canceledCount={commitCanceledCount}
                />
            );
        }

        if (folderStep === 'login') {
            return (
                <EnvelopeFolderLogin
                    folderHash={folderHash!}
                    folderData={folderData}
                    setFolderData={setFolderData}
                    setFolderStep={setFolderStep}
                    setFolderSessionToken={setFolderSessionToken}
                    folderSessionToken={folderSessionToken}
                    latitude={latitude}
                    longitude={longitude}
                    buildAuthHeaders={buildAuthHeaders}
                    applyListing={applyListing}
                />
            );
        }

        if (folderStep === 'proposals') {
            const resolvedTheme: QISignTheme = clientThemeSettings ?? DEFAULT_CLIENT_THEME_SETTINGS;

            if (selectedEnvelopeKey) {
                if (loadingDetail) {
                    return <PrivatePayrollAuctionLoading />;
                }

                const envelopes: any[] = Array.isArray(listing?.envelopes) ? listing.envelopes : [];
                const listedEnvelope = envelopes.find(
                    (envelope) => envelope.envelope_key === selectedEnvelopeKey,
                );
                const selectedEnvelope = (listedEnvelope || selectedDetail)
                    ? { ...(listedEnvelope ?? {}), ...(selectedDetail ?? {}) }
                    : undefined;

                if (selectedEnvelope) {
                    const clearSelection = () => {
                        setSelectedEnvelopeKey(undefined);
                        setSelectedDetail(undefined);
                    };

                    const conditionalStep: string =
                        selectedEnvelope.gate_step ?? selectedEnvelope.next_step ?? 'resume_operation';
                    const customTheme: string | undefined =
                        selectedEnvelope.signature_settings?.custom_theme ?? 'social_security';
                    const theme =
                        customTheme !== 'private_payroll_auction'
                            ? SocialSecurityTheme
                            : PrivatePayrollAuctionTheme;

                    const stepProps = {
                        signData: selectedEnvelope,
                        setSignerStep: (step: string) => {
                            if (step === 'login') {
                                clearSelection();
                                setFolderStep('login');
                            }
                        },
                        setSignData: (data: any) =>
                            setSelectedDetail((prev: any) => ({ ...(prev ?? {}), ...data })),
                        folderMode: {
                            onBack: clearSelection,
                            currentDecision: selectedEnvelope.listing_status,
                            submitDecision: (decision: 'accepted' | 'rejected') =>
                                submitFolderDecision(selectedEnvelopeKey, decision, conditionalStep),
                            refreshDetail: () => refreshProposalDetail(selectedEnvelopeKey),
                        },
                    };

                    if (conditionalStep === 'portability_and_refinancing_summary') {
                        return <theme.PortabilityAndRefinancingSummary {...stepProps} />;
                    }
                    if (conditionalStep === 'refinancing_summary') {
                        return <theme.RefinancingSummary {...stepProps} />;
                    }

                    return <theme.ResumeOperation {...stepProps} />;
                }
            }

            return (
                <EnvelopeFolderProposals
                    listing={listing}
                    resolvedTheme={resolvedTheme}
                    onSelectProposal={(envelope) => {
                        if (envelope.listing_status === 'in_progress') {
                            openChildSession(envelope.envelope_key);
                            return;
                        }
                        openProposalDetail(envelope.envelope_key);
                    }}
                    onCommit={handleCommit}
                />
            );
        }

        if (
            folderStep === 'face_recognition' ||
            folderStep === 'personal_document' ||
            folderStep === 'authentication_code'
        ) {
            const resolvedTheme: QISignTheme = clientThemeSettings ?? DEFAULT_CLIENT_THEME_SETTINGS;
            return (
                <EnvelopeFolderCollection
                    folderSessionToken={folderSessionToken}
                    resolvedTheme={resolvedTheme}
                    listing={listing}
                    initialStep={folderStep as 'face_recognition' | 'personal_document' | 'authentication_code'}
                    setSignMetadata={setSignMetadata}
                    buildAuthHeaders={buildAuthHeaders}
                    refreshListing={refreshListing}
                    setSessionExpired={() => setFolderStep('login')}
                    onCollectionComplete={() => setFolderStep('signed')}
                />
            );
        }

        return <NotFoundPage />;
    };

    return <ErrorBoundaryComponent>{getScenes()}</ErrorBoundaryComponent>;
};
